import { useTu } from "@src/appV2/i18n/useTu";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Card, CardContent, CardHeader, Stack } from "@mui/material";
import { CbhIcon } from "@src/appV2/components/CbhIcon/CbhIcon";
import { HyperTrackEmbeddedView } from "@src/appV2/ShiftBoard/ShiftCard/HyperTrackEmbeddedView";
import { addMinutes, formatDistanceToNow, isAfter } from "date-fns";
import { Text } from "../../components/Text";
import { NO_SHOW_WARNING_TYPE, type NoShowWarningType } from "../utils/getNoShowWarningType";
const METERS_PER_MILE = 1609.34;
const STALE_LOCATION_THRESHOLD_MINUTES = 5;

interface NoShowWarningContentProps {
  warningType: NoShowWarningType;
  distanceInM: number;
  etaDurationInMin: number;
  lastLocationReceivedByServerAt?: string;
  trackingUrl?: string;
}

export function NoShowWarningContent(props: NoShowWarningContentProps): JSX.Element {
  const { tu } = useTu("shift");

  const {
    warningType,
    distanceInM,
    etaDurationInMin,
    lastLocationReceivedByServerAt,
    trackingUrl,
  } = props;

  const distanceInMiles = Math.round(distanceInM / METERS_PER_MILE);
  const etaMinutes = Math.round(etaDurationInMin);

  const isLocationStale =
    isDefined(lastLocationReceivedByServerAt) &&
    isAfter(
      addMinutes(new Date(), -STALE_LOCATION_THRESHOLD_MINUTES),
      new Date(lastLocationReceivedByServerAt)
    );

  const lastUpdatedText = isDefined(lastLocationReceivedByServerAt)
    ? `${formatDistanceToNow(new Date(lastLocationReceivedByServerAt))} ago`
    : tu("N/A");

  const warningMessage =
    warningType === NO_SHOW_WARNING_TYPE.workerNearby
      ? tu(
          "Before you mark No Show, we wanted to flag that the worker seems to be within a mile of your workplace."
        )
      : tu(
          "Before you mark No Show, we wanted to flag that the worker seems to be moving towards your workplace."
        );

  return (
    <Stack spacing={3}>
      <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
        {warningMessage}
      </Text>
      <Card
        variant="outlined"
        sx={(theme) => ({ backgroundColor: theme.palette.background.default })}
      >
        <CardHeader
          title={
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" gap={1} alignItems="center">
                <CbhIcon type="navigation-arrow" size="small" />
                <Text variant="h5">{tu("Live tracking")}</Text>
              </Stack>

              <Stack alignItems="flex-end">
                <Stack direction="row" gap={2}>
                  <Text variant="body2">
                    {tu("Distance:")}{" "}
                    <Box component="span" sx={(theme) => ({ color: theme.palette.text.primary })}>
                      {tu("{{distanceInMiles}} mi", { values: { distanceInMiles } })}
                    </Box>
                  </Text>
                  <Text variant="body2">
                    {tu("ETA:")}{" "}
                    <Box component="span" sx={(theme) => ({ color: theme.palette.text.primary })}>
                      {tu("{{etaMinutes}} mins", { values: { etaMinutes } })}
                    </Box>
                  </Text>
                </Stack>
                <Text variant="body2">
                  {tu("Last Updated: {{lastUpdatedText}}", { values: { lastUpdatedText } })}
                  {isLocationStale && (
                    <Box
                      component="span"
                      sx={(theme) => ({
                        color: theme.palette.error.main,
                        fontStyle: "italic",
                        marginLeft: 0.5,
                      })}
                    >
                      {tu("We seem to have lost connection.")}
                    </Box>
                  )}
                </Text>
              </Stack>
            </Stack>
          }
        />

        <CardContent sx={{ paddingTop: 0 }}>
          <HyperTrackEmbeddedView trackingUrl={trackingUrl} aspectRatio="4 / 3" />
        </CardContent>
      </Card>
    </Stack>
  );
}
